'use client';

/**
 * CategorySliderBlock — horizontal slider of course-category tiles
 * (icon/image + name), pulled from {@code LearningSource.fetchCategories()}
 * or from operator-supplied {@code items}.
 *
 * <p>Three tile-size presets ({@code small}/{@code medium}/{@code large})
 * and a configurable image {@code aspectRatio} cover the common "category
 * shortcuts" row pattern (see legacy {@code CategorySmallSliderBox}).
 *
 * <p>Layout / interaction is delegated to the shared {@link ContentRail}
 * primitive — same {@code scrollMode} vocabulary as {@code ComingSoon}
 * ('grid' | 'drag' | 'buttons' | 'scrollbar' | 'wheel'), so no separate
 * carousel engine (e.g. swiper) is introduced.
 */
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import NextImage from 'next/image';
import { useLearningSource, type LearningCategory } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { cardReset } from '../_shared/styles';
import type { PaddingBox } from '../_shared/resolvePadding';
import { ContentRail, type ContentRailScrollMode } from '../ui/ContentRail';

/** Card layout / scroll behaviour — same vocabulary as {@code ComingSoonScrollMode}. */
export type CategorySliderScrollMode = 'grid' | 'drag' | 'buttons' | 'scrollbar' | 'wheel';

/** Tile size preset — controls item width, image height and label size. */
export type CategorySliderSize = 'small' | 'medium' | 'large';

export interface CategorySliderItem {
  id?: string | number;
  slug?: string;
  name: string;
  image?: string;
}

export interface CategorySliderBlockProps {
  title?: string;
  titleFontSize?: string;
  limit?: number;
  size?: CategorySliderSize;
  /** Image aspect ratio, e.g. "1/1", "4/3", "16/9". */
  aspectRatio?: string;
  scrollMode?: CategorySliderScrollMode;
  hrefTemplate?: string;
  /** Operator-supplied items instead of the live category feed. */
  items?: CategorySliderItem[];
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const SIZE_SPEC: Record<CategorySliderSize, { itemWidth: string; gap: string; font: string }> = {
  small: { itemWidth: '88px', gap: 'var(--space-3, 12px)', font: 'var(--font-size-xs, 12px)' },
  medium: { itemWidth: '150px', gap: 'var(--space-4, 16px)', font: 'var(--font-size-sm, 14px)' },
  large: { itemWidth: '220px', gap: 'var(--space-5, 20px)', font: 'var(--font-size-md, 16px)' },
};

function toItem(c: LearningCategory): CategorySliderItem {
  const item: CategorySliderItem = { id: c.id, name: c.name };
  if (c.slug !== undefined) item.slug = c.slug;
  if (c.imageUrl !== undefined) item.image = c.imageUrl;
  return item;
}

export function CategorySliderBlock({
  title,
  titleFontSize,
  limit = 12,
  size = 'small',
  aspectRatio = '1/1',
  scrollMode = 'grid',
  hrefTemplate = '/lxp/catalog?category={id}',
  items: customItems,
  paddingOuter,
  paddingInner,
}: CategorySliderBlockProps) {
  const { t } = useTranslation('site-builder-blocks');
  const learning = useLearningSource();
  // limit === 0 means "show all" — no slicing.
  const take = <T,>(list: T[]): T[] => (limit > 0 ? list.slice(0, limit) : list);

  const state = useAsyncResource<CategorySliderItem[]>(async () => {
    if (customItems && customItems.length > 0) return take(customItems);
    if (!learning) return null;
    const cats = await learning.fetchCategories();
    return cats.length > 0 ? take(cats).map(toItem) : null;
  }, [learning, customItems, limit]);

  if (!learning && (!customItems || customItems.length === 0)) {
    return (
      <SectionShell title={title} titleFontSize={titleFontSize}>
        <AdapterPlaceholder blockType="CategorySliderBlock" adapterName="LearningSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title} titleFontSize={titleFontSize}>
        <SkeletonRow
          count={limit > 0 ? limit : 8}
          layout="rail"
          minCardWidth={parseInt(SIZE_SPEC[size].itemWidth, 10)}
        />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={title} titleFontSize={titleFontSize}>
        <EmptyState
          title={t('shared.empty.categories.title')}
          hint={t('shared.empty.categories.hint')}
        />
      </SectionShell>
    );
  }

  const spec = SIZE_SPEC[size];
  const cards = state.data.map((item, i) => (
    <CategoryTile
      key={`${item.id ?? item.slug ?? item.name}-${i}`}
      item={item}
      aspectRatio={aspectRatio}
      font={spec.font}
      hrefTemplate={hrefTemplate}
    />
  ));

  const headerArrows = scrollMode === 'buttons';

  return (
    <SectionShell
      title={headerArrows ? undefined : title}
      titleFontSize={titleFontSize}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      {scrollMode === 'grid' ? (
        <>
          <style>{`
            .cat-slider-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(${spec.itemWidth}, 1fr));
              gap: ${spec.gap};
            }
          `}</style>
          <div className="cat-slider-grid">{cards}</div>
        </>
      ) : (
        <ContentRail
          ariaLabel={title || 'Categories'}
          itemWidth={spec.itemWidth}
          gap={spec.gap}
          scrollMode={scrollMode as ContentRailScrollMode}
          heading={headerArrows ? title : undefined}
          peek={false}
          edgeFade
        >
          {cards}
        </ContentRail>
      )}
    </SectionShell>
  );
}

function CategoryTile({
  item,
  aspectRatio,
  font,
  hrefTemplate,
}: {
  item: CategorySliderItem;
  aspectRatio: string;
  font: string;
  hrefTemplate: string;
}) {
  const slugKey = String(item.slug ?? item.id ?? item.name);
  const idKey = String(item.id ?? item.slug ?? item.name);
  const href = hrefTemplate
    .replace('{slug}', encodeURIComponent(slugKey))
    .replace('{id}', encodeURIComponent(idKey));
  return (
    <Card asChild>
      <Link
        href={href}
        title={item.name}
        style={{
          ...cardReset,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <div
          aria-hidden
          style={{
            width: '100%',
            aspectRatio,
            borderRadius: 'var(--radius-lg, 16px)',
            position: 'relative',
            overflow: 'hidden',
            background: item.image ? undefined : 'var(--color-neutral-200, #e4e4e7)',
            boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.08))',
          }}
        >
          {item.image && (
            <NextImage
              src={item.image}
              alt=""
              fill
              sizes="(max-width: 640px) 40vw, 220px"
              style={{ objectFit: 'cover' }}
            />
          )}
        </div>
        <div
          style={{
            width: '100%',
            fontSize: font,
            fontWeight: 600,
            textAlign: 'center',
            color: 'var(--color-text-primary, #18181b)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name}
        </div>
      </Link>
    </Card>
  );
}
